import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { updateSection, deleteSection, createLesson, updateLesson } from '../../actions/certificationActions'
import { reorderLessons } from '../../reducers/certificationBuilderReducer'
import { blurOnEnter, showGenericError } from '../../utility/certificationBuilder'
import { useBuilderSensors, sortableStyle, dragEndIds } from './sortable'
import DragHandle from './DragHandle'
import LessonRow from './LessonRow'
import CurriculumRow from './CurriculumRow'
import DeleteConfirmationModal from './DeleteConfirmationModal'

import classes from './SectionCard.module.scss'

const SectionCard = ({ certificationId, section, number, selection, onSelect }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const sensors = useBuilderSensors()
  const sortable = useSortable({ id: section.id })
  const [open, setOpen] = useState(true)
  const [title, setTitle] = useState(section.title)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const quizSelected = selection?.type === 'quiz' && selection.id === section.quiz?.id

  const saveTitle = () => {
    if (title.trim() === section.title) { setTitle(section.title); return }

    dispatch(updateSection({ certificationId, id: section.id, title: title.trim() })).unwrap().catch(showGenericError)
  }

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deleteSection({ certificationId, id: section.id })).unwrap()
      .then(() => { if (selection?.sectionId === section.id) { onSelect(null) } })
      .catch(() => { showGenericError(); setIsDeleting(false) })
  }

  const handleLessonDragEnd = (event) => {
    const ids = dragEndIds(event)
    if (!ids) { return }

    dispatch(reorderLessons({ sectionId: section.id, ...ids }))
    const position = section.lessons.findIndex(lesson => lesson.id === ids.overId) + 1
    dispatch(updateLesson({ sectionId: section.id, id: ids.activeId, position })).unwrap().catch(showGenericError)
  }

  const addLesson = () => dispatch(createLesson({ sectionId: section.id, title: '' })).unwrap()
    .then(({ lesson }) => { setOpen(true); onSelect({ type: 'lesson', id: lesson.id, sectionId: section.id }) })
    .catch(showGenericError)

  const addQuiz = () => dispatch(updateSection({ certificationId, id: section.id, quizAttributes: {} })).unwrap()
    .then(({ section: saved }) => { setOpen(true); onSelect({ type: 'quiz', id: saved.quiz.id, sectionId: section.id }) })
    .catch(showGenericError)

  return (
    <div ref={sortable.setNodeRef} style={sortableStyle(sortable)} className={classNames(classes.card, { [classes.cardOpen]: open })}>
      <div className={classNames('d-flex align-items-center', classes.head, { [classes.headOpen]: open })}>
        <DragHandle listeners={sortable.listeners} attributes={sortable.attributes} />
        <span className={classNames('d-flex align-items-center justify-content-center', classes.number)}>{number}</span>
        <div className={classNames('flex-grow-1', classes.titleColumn)}>
          <input
            className={classes.titleInput}
            value={title}
            placeholder={t('certificationBuilder.curriculum.sectionTitlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={blurOnEnter}
          />
          <div className={classes.meta}>
            {t('certificationBuilder.curriculum.sectionMeta', { count: section.lessons.length })}
            {section.quiz && ` · ${t('certificationBuilder.curriculum.quiz')}`}
          </div>
        </div>
        <span className={classNames('d-inline-flex align-items-center', classes.quizPill, { [classes.quizPillMissing]: !section.quiz })}>
          <i className={section.quiz ? 'fa fa-check-square-o' : 'fa fa-square-o'} aria-hidden='true' />
          {t('certificationBuilder.curriculum.quiz')}
        </span>
        <button className={classes.iconButton} title={t('certificationBuilder.sections.delete')} onClick={() => setShowDeleteModal(true)}>
          <i className='fa fa-trash-o' aria-hidden='true' />
        </button>
        <button className={classes.iconButton} onClick={() => setOpen(!open)} aria-expanded={open}>
          <i className={open ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} aria-hidden='true' />
        </button>
      </div>

      {open && (
        <div className={classes.body}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLessonDragEnd}>
            <SortableContext items={section.lessons.map(lesson => lesson.id)} strategy={verticalListSortingStrategy}>
              {section.lessons.map(lesson => (
                <LessonRow
                  key={lesson.id}
                  lesson={lesson}
                  selected={selection?.type === 'lesson' && selection.id === lesson.id}
                  onSelect={() => onSelect({ type: 'lesson', id: lesson.id, sectionId: section.id })}
                />
              ))}
            </SortableContext>
          </DndContext>

          <CurriculumRow
            icon={<i className={section.quiz ? 'fa fa-check-square-o' : 'fa fa-square-o'} aria-hidden='true' />}
            iconVariant={section.quiz ? 'quiz' : 'missing'}
            title={t('certificationBuilder.curriculum.sectionQuiz', { number })}
            muted={!section.quiz}
            meta={section.quiz
              ? t('certificationBuilder.curriculum.quizMeta', { count: section.quiz.questions.length, passMark: section.quiz.passMark })
              : t('certificationBuilder.curriculum.noQuiz')}
            selected={quizSelected}
            onSelect={section.quiz ? () => onSelect({ type: 'quiz', id: section.quiz.id, sectionId: section.id }) : addQuiz}
          />

          <div className={classes.actions}>
            <button className={classes.dashedPill} onClick={addLesson}>
              <i className='fa fa-plus' aria-hidden='true' />
              {t('certificationBuilder.curriculum.addLesson')}
            </button>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        show={showDeleteModal}
        title={t('certificationBuilder.sections.deleteTitle', { number })}
        message={t('certificationBuilder.sections.deleteMessage', {
          title: section.title || t('certificationBuilder.curriculum.untitledSection'), count: section.lessons.length,
        })}
        isLoading={isDeleting}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDelete}
      />
    </div>
  )
}

SectionCard.propTypes = {
  certificationId: PropTypes.number.isRequired,
  section: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    lessons: PropTypes.array.isRequired,
    quiz: PropTypes.shape({
      id: PropTypes.number.isRequired,
      passMark: PropTypes.number.isRequired,
      questions: PropTypes.array.isRequired,
    }),
  }).isRequired,
  number: PropTypes.number.isRequired,
  selection: PropTypes.shape({
    type: PropTypes.string.isRequired,
    id: PropTypes.number,
    sectionId: PropTypes.number,
  }),
  onSelect: PropTypes.func.isRequired,
}

export default SectionCard
